import { BY_ID } from '../game/roster.js';
import { clamp, normalize } from '../game/math.js';

export class Input {
  keys=new Set();_enabled=false;touchBindings=[];pointer={x:0,y:0};mouseAim=false;mouseDown=false;fireOnce=false;superOnce=false;gadgetOnce=false;move={x:0,z:0};aim={x:0,z:-1};touchAim=false;touchPower=1;
  get enabled(){return this._enabled;}
  set enabled(value){if(this._enabled&&!value)this.reset();this._enabled=!!value;}
  constructor(world,onPause){
    this.world=world;this.onPause=onPause;
    addEventListener('keydown',event=>{
      if(event.target.closest?.('input,textarea,select,[contenteditable=true]')||!this.enabled)return;
      if(['KeyW','KeyA','KeyS','KeyD','ArrowUp','ArrowDown','ArrowLeft','ArrowRight','Space','KeyQ','KeyE'].includes(event.code))event.preventDefault();
      this.keys.add(event.code);if(event.repeat)return;
      if(event.code==='KeyQ'||event.code==='Space')this.superOnce=true;
      if(event.code==='KeyE')this.gadgetOnce=true;
      if(event.code==='Escape')this.onPause();
    });
    addEventListener('keyup',event=>this.keys.delete(event.code));
    addEventListener('pointermove',event=>{if(event.pointerType==='mouse'){this.pointer={x:event.clientX,y:event.clientY};this.world.pointer={x:event.clientX/innerWidth*2-1,y:event.clientY/innerHeight*2-1};if(this.enabled)this.mouseAim=true;}});
    world.canvas.addEventListener('pointerdown',event=>{if(event.pointerType==='mouse'&&event.button===0&&this.enabled){this.mouseDown=true;this.mouseAim=true;}});
    addEventListener('pointerup',event=>{if(event.pointerType==='mouse')this.mouseDown=false;});
    addEventListener('blur',()=>this.reset());document.addEventListener('visibilitychange',()=>{if(document.hidden)this.reset();});
    world.canvas.addEventListener('contextmenu',event=>{if(this.enabled)event.preventDefault();});
    addEventListener('resize',()=>this.reset());
  }
  reset(){
    this.keys.clear();this.mouseDown=false;this.superOnce=false;this.gadgetOnce=false;
    this.fireOnce=false;this.move={x:0,z:0};this.touchAim=false;this.touchPower=1;
    for(const binding of this.touchBindings)binding.cancel();
  }
  bindTouch(){
    for(const binding of this.touchBindings)binding.dispose();
    this.touchBindings=[];
    for(const kind of ['move','aim']){
      const element=document.querySelector('#'+kind+'-stick');if(!element)continue;
      let pointerId=null,origin={x:0,y:0};
      const listeners=[];
      const listen=(type,handler)=>{element.addEventListener(type,handler);listeners.push([type,handler]);};
      const cancel=()=>{
        const id=pointerId;pointerId=null;
        if(id!==null&&element.hasPointerCapture(id))element.releasePointerCapture(id);
        element.firstElementChild.style.transform='';
        if(kind==='move')this.move={x:0,z:0};else this.touchAim=false;
      };
      const update=event=>{
        const dx=event.clientX-origin.x,dy=event.clientY-origin.y;
        const length=Math.hypot(dx,dy),scale=Math.min(1,42/(length||1));
        element.firstElementChild.style.transform=`translate(${dx*scale}px,${dy*scale}px)`;
        if(kind==='move')this.move={x:dx*scale/42,z:dy*scale/42};
        else{
          if(length>5)this.aim=normalize(dx,dy);
          this.touchPower=clamp(length/42,.15,1);this.touchAim=true;this.mouseAim=false;
        }
      };
      listen('pointerdown',event=>{
        // Do not use isPrimary: aiming must work while a second finger is moving.
        if(!this.enabled||pointerId!==null||event.button!==0)return;
        event.preventDefault();pointerId=event.pointerId;element.setPointerCapture(pointerId);
        const rect=element.getBoundingClientRect();origin={x:rect.left+rect.width/2,y:rect.top+rect.height/2};update(event);
      });
      listen('pointermove',event=>{if(event.pointerId===pointerId&&this.enabled)update(event);});
      listen('pointerup',event=>{
        if(event.pointerId!==pointerId)return;
        if(kind==='aim'&&this.enabled)this.fireOnce=true;
        cancel();
      });
      // A system gesture, rotation, lost capture or menu must not become a shot,
      // and canceling one stick must not drop the finger on the other stick.
      for(const type of ['pointercancel','lostpointercapture'])listen(type,event=>{if(event.pointerId===pointerId)cancel();});
      this.touchBindings.push({cancel,dispose:()=>{cancel();for(const [type,handler]of listeners)element.removeEventListener(type,handler);}});
    }
  }
  sample(state,id){
    if(!this.enabled)return{x:0,z:0,ax:this.aim.x,az:this.aim.z,fire:false,super:false,gadget:false,aim:1,aiming:false};
    let x=this.move.x+(this.keys.has('KeyD')||this.keys.has('ArrowRight')?1:0)-(this.keys.has('KeyA')||this.keys.has('ArrowLeft')?1:0);
    let z=this.move.z+(this.keys.has('KeyS')||this.keys.has('ArrowDown')?1:0)-(this.keys.has('KeyW')||this.keys.has('ArrowUp')?1:0);
    const size=Math.hypot(x,z);if(size>1){x/=size;z/=size;}
    const me=state?.players.find(p=>p.id===id);let aim=this.aim,power=this.touchPower;
    if(me&&this.mouseAim){const at=this.world.worldPoint(this.pointer.x,this.pointer.y);if(at){aim=normalize(at.x-me.x,at.z-me.z);power=clamp(Math.hypot(at.x-me.x,at.z-me.z)/BY_ID[me.character].range,.15,1);}}
    else if(me&&!this.touchAim&&(!this.fireOnce||this.touchPower<.3)){
      const target=state.players.filter(p=>p.hp>0&&p.team!==me.team).sort((a,b)=>Math.hypot(a.x-me.x,a.z-me.z)-Math.hypot(b.x-me.x,b.z-me.z))[0];
      if(target){aim=normalize(target.x-me.x,target.z-me.z);power=clamp(Math.hypot(target.x-me.x,target.z-me.z)/BY_ID[me.character].range,.15,1);}
    }
    this.aim=aim;const result={x,z,ax:aim.x,az:aim.z,fire:this.mouseDown||this.fireOnce,super:this.superOnce,gadget:this.gadgetOnce,aim:power,aiming:this.mouseAim||this.touchAim};
    this.fireOnce=false;this.superOnce=false;this.gadgetOnce=false;return result;
  }
}
